<script lang="ts">
    import { Component, Prop, Vue, Watch } from "vue-property-decorator";
    import EditModal from "./EditModal.vue";
    import DarkDropdown from "./DarkDropdown.vue";
    import ColorSelector from "./ColorSelector.vue";
    @Component({
        components: {
            EditModal,
            DarkDropdown,
            ColorSelector,
        },
    })
    export default class SeriesModal extends Vue {
        defaultPosition = {
            left: 320,
            top: 380,
        };
        @Prop()
        legend;
        @Prop()
        colorList;
        handleClose() {
            this.$emit("closeEvent");
        }
        callColorSelector(e: MouseEvent) {
            let x = e.pageX - e.offsetX,
                y = e.pageY - e.offsetY,
                w = (e.target as HTMLElement).clientWidth,
                h = (e.target as HTMLElement).clientHeight;
            (this.$refs.colorSelector as any).showSelector(this.activeColor, {
                left: x,
                top: y + h,
            });
        }
        setColor(str: string) {
            this.activeColor = str;
            this.$emit("changeColor", {
                index: this.activeColorIndex,
                color: str,
            });
        }
        displaySeries = [];
        get displayPosition() {
            return [
                {
                    label: this.$t("hidden"),
                    value: "hidden",
                },
                {
                    label: this.$t("topcenter"),
                    value: "topCenter",
                },
                {
                    label: this.$t("righttop"),
                    value: "topRight",
                },
                {
                    label: this.$t("rightcenter"),
                    value: "middleRight",
                },
                {
                    label: this.$t("bottomcenter"),
                    value: "bottomCenter",
                },
            ];
        }
        checkLegendPostion() {
            const legend = this.legend;
            if (legend["show"]) {
                if (legend["top"] === "top") {
                    if (legend["left"] === "center") {
                        this.activePostion = 1;
                    } else if (legend["right"] === 0) {
                        this.activePostion = 2;
                    }
                } else if (legend["top"] === "middle") {
                    if (legend["right"] === 0) {
                        this.activePostion = 3;
                    }
                } else if (legend["top"] === "bottom") {
                    this.activePostion = 4;
                }
            } else {
                this.activePostion = 0;
            }
            (this.$refs.positionBox as any).setActiveIndex(this.activePostion);
        }
        created() {
            this.displaySeries = this.colorList.map((item: any) => {
                return {
                    label: item.name,
                    value: item.name,
                    color: item.color,
                };
            });
            if (this.displaySeries.length) {
                this.activeColor = this.displaySeries[0].color;
            }
        }
        mounted() {
            this.checkLegendPostion();
        }
        activeColor = "#fff";
        activePostion = 0;
        activeColorIndex = 0;
        changeSeries(index: number) {
            this.activeColorIndex = index;
            this.activeColor = this.displaySeries[index].color;
        }
        changePosition(index: number) {
            this.$emit("changeLegend", this.displayPosition[index].value);
        }
    }
</script>

<template>
    <EditModal :title="$t('title')" :width="176" :position="defaultPosition" @closeEvent="handleClose">
        <div class="dropdown-item">
            <p class="dropdown-title textoverflow" :title="$t('position')">{{ $t("position") }}</p>
            <DarkDropdown ref="positionBox" class="dropdown-box" :data="displayPosition" :defaultIndex="activePostion" @selectData="changePosition" />
        </div>
        <div class="dropdown-item">
            <p class="dropdown-title textoverflow" :title="$t('color')">{{ $t("color") }}</p>
            <DarkDropdown class="dropdown-box color-dropdown" :data="displaySeries" :defaultIndex="0" @selectData="changeSeries" />
            <div class="dropdown-color" :style="{ background: activeColor }" @click.stop="callColorSelector"></div>
        </div>
        <ColorSelector ref="colorSelector" @setColor="setColor" />
    </EditModal>
</template>

<style lang="stylus" scoped>
    .dropdown-item
        position relative
        display flex
        justify-content space-between
        align-items center
        padding 0 4px
        &:not(.last-child)
            margin-bottom 4px
        .dropdown-box
            width 96px

        .color-dropdown /deep/
            .z-dropdown-title
                padding-right 30px
        .dropdown-title
            margin-right 12px
            font-size 12px
            color #fff
            max-width 48px
        .dropdown-color
            position absolute
            right 20px
            top 4px
            width 24px
            height 12px
            border-radius 4px
            cursor pointer
</style>
<i18n>
{
    "zh" : {
        "title": "图例设置",
        "position": "图例位置",
        "hidden": "隐藏",
        "topcenter": "顶部中间",
        "righttop": "右侧顶部",
        "rightcenter": "右侧中间",
        "bottomcenter": "底部中间",
        "color": "图例颜色"
    },
    "en": {
        "title": "Legend Settings",
        "position": "Legend Position",
        "hidden": "Hide",
        "topcenter": "Top Center",
        "righttop": "Top Right",
        "rightcenter": "Middle Right",
        "bottomcenter": "Bottom Center",
        "color": "Color"
    },
    "de" : {
        "title": "Legendeneinstellungen",
        "position": "Legendenposition",
        "hidden": "Verbergen",
        "topcenter": "Oben in der Mitte",
        "righttop": "Oben rechts",
        "rightcenter": "Mitte rechts",
        "bottomcenter": "Mitte unten",
        "color": "Farbe"
    },
    "es" : {
        "title": "Ajustes de leyenda",
        "position": "Posición de la leyenda",
        "hidden": "Ocultar",
        "topcenter": "Top Center",
        "righttop": "Arriba a la derecha",
        "rightcenter": "Medio derecha",
        "bottomcenter": "Centro inferior",
        "color": "Color"
    },
    "fr" : {
        "title": "Paramètres de légende",
        "position": "Position de la légende",
        "hidden": "Caché",
        "topcenter": "Centre Supérieur",
        "righttop": "En haut à droite",
        "rightcenter": "Milieu Droit",
        "bottomcenter": "En bas au centre",
        "color": "couleur"
    },
    "id" : {
        "title": "Pengaturan Keterangan",
        "position": "posisi legenda",
        "hidden": "bersembunyi",
        "topcenter": "tengah atas",
        "righttop": "kanan atas",
        "rightcenter": "kanan tengah",
        "bottomcenter": "tengah bawah",
        "color": "warna"
    },
    "it" : {
        "title": "Impostazioni legenda",
        "position": "Posizione Legenda",
        "hidden": "Nascondi",
        "topcenter": "In Alto al Centro",
        "righttop": "In Alto a Destra",
        "rightcenter": "Centro - Destra",
        "bottomcenter": "In Basso al Centro",
        "color": "Colore"
    },
    "jp" : {
        "title": "凡例の設定",
        "position": "凡例の位置",
        "hidden": "非表示",
        "topcenter": "上中央",
        "righttop": "右上",
        "rightcenter": "右中央",
        "bottomcenter": "下中央",
        "color": "色"
    },
    "kr" : {
        "title": "범례 설정",
        "position": "레전드 위치",
        "hidden": "숨다",
        "topcenter": "상단 중간",
        "righttop": "오른쪽 상단",
        "rightcenter": "가운데 오른쪽",
        "bottomcenter": "하단 중앙",
        "color": "색상"
    },
    "pt" : {
        "title": "Configurações da legenda",
        "position": "Posição da Legenda",
        "hidden": "Esconder",
        "topcenter": "Centro Superior",
        "righttop": "Direito Superior",
        "rightcenter": "Meio Direita",
        "bottomcenter": "Centro Inferior",
        "color": "Cor"
    },
    "tw" : {
        "title": "圖例設定",
        "position": "圖例位置",
        "hidden": "隱藏",
        "topcenter": "頂部中間",
        "righttop": "右側頂部",
        "rightcenter": "右側中間",
        "bottomcenter": "底部中間",
        "color": "顏色"
    }
}
</i18n>
